import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { GfMultiImage } from "@swagger/typescript-fetch-goflyer";
import { adminApiServiceRaw } from "../../../../../utils/api";
import { FlyerItemWithExtras } from "../../types";
import { reviewKeys } from "../../queryKeys";

export interface ItemSummary {
  id: string;
  name: string;
  nameChinese?: string;
  thumbnailUrl?: string;
  rank?: number;
  salePrice?: number;
  regularPrice?: number;
  error?: boolean;
}

const pickThumbnail = (
  multiImages?: GfMultiImage[] | null,
): string | undefined => {
  if (!multiImages || multiImages.length === 0) return undefined;
  for (const m of multiImages) {
    const src = (m as { gfImage?: { src?: string } })?.gfImage?.src;
    if (src) return src;
  }
  return undefined;
};

export const flyerItemToSummary = (item: FlyerItemWithExtras): ItemSummary => ({
  id: item.id,
  name: item.name || item.nameChinese || "",
  nameChinese: item.nameChinese || undefined,
  thumbnailUrl: pickThumbnail(item.gfMultiImages),
  rank: item.rank,
  salePrice: item.salePrice,
  regularPrice: item.regularPrice,
});

const SUMMARY_STALE_TIME = 10 * 60_000;
const SUMMARY_GC_TIME = 15 * 60_000;

const summaryQueryOptions = (id: string, authToken: string) => ({
  queryKey: reviewKeys.itemSummary(id),
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    adminApiServiceRaw.getOneBaseGfFlyerItemAdminControllerGfFlyerItem(
      id,
      undefined,
      undefined,
      undefined,
      { headers: { Authorization: authToken }, signal },
    ),
  enabled: !!id,
  staleTime: SUMMARY_STALE_TIME,
  gcTime: SUMMARY_GC_TIME,
  select: (r: unknown) => flyerItemToSummary(r as FlyerItemWithExtras),
});

/**
 * Batch summary lookup. Each id is its own react-query entry, so:
 *  - identical ids requested from anywhere (this hook, the per-id hook, the
 *    viewport-lazy rows) are de-duplicated and cached for 10 minutes, and
 *  - a new `flyerItemIds` array identity no longer triggers a refetch storm.
 *
 * The `Map<string, ItemSummary | "loading">` return shape is unchanged so all
 * existing consumers work without modification.
 */
export const useFlyerItemSummaries = (
  flyerItemIds: string[],
  reqOptions: { headers: { Authorization: string } },
): Map<string, ItemSummary | "loading"> => {
  const authToken = reqOptions.headers.Authorization;

  const results = useQueries({
    queries: flyerItemIds.map((id) => summaryQueryOptions(id, authToken)),
  });

  // Stable signature so the Map keeps referential identity across renders
  // unless an id set / fetch state actually changed (helps memoized rows).
  const signature = flyerItemIds
    .map((id, i) => `${id}:${results[i]?.status ?? ""}`)
    .join("|");

  return useMemo(() => {
    const map = new Map<string, ItemSummary | "loading">();
    flyerItemIds.forEach((id, i) => {
      const q = results[i];
      if (!q) return;
      if (q.isError) {
        map.set(id, { id, name: "", error: true });
      } else if (q.data) {
        map.set(id, q.data);
      } else {
        map.set(id, "loading");
      }
    });
    return map;
    // `signature` already encodes every id + fetch-state change that can
    // affect the map, so it is the only dependency required here.
  }, [signature]);
};

/**
 * Single-item summary, sharing the exact same cache entry as the batch hook
 * above. Intended for viewport-lazy rows that fetch only when visible.
 */
export const useFlyerItemSummary = (
  flyerItemId: string | undefined,
  reqOptions: { headers: { Authorization: string } },
  options?: { enabled?: boolean },
): ItemSummary | "loading" | undefined => {
  const authToken = reqOptions.headers.Authorization;
  const query = useQuery({
    ...summaryQueryOptions(flyerItemId ?? "", authToken),
    enabled: !!flyerItemId && options?.enabled !== false,
  });
  if (!flyerItemId) return undefined;
  if (query.isError) return { id: flyerItemId, name: "", error: true };
  return query.data ?? "loading";
};
